/**
 * What a media_server sends a player over WebRTC: the signalling socket's frames and the data
 * channel's. nx_webrtc builds both in C++, and the cloud relays the socket byte for byte.
 * Neither stream has a type field: each kind of frame has a key only it carries.
 */

import { z } from 'zod';

import { keyBy, listOf } from '@/api/listOf';

import type { KeyedFrame } from './contract';
import { DeviceIdSchema } from './deviceId';
import type { MetadataPacket } from './metadataPacket';

/** A session description to answer; renegotiations come the same way. */
export const OfferFrameSchema = z.object({
    sdp: z.object({
        type: z.literal('offer'),
        sdp: z.string(),
    }),
    /**
     * Goes back with the answer, so the server can drop an answer to a replaced offer. A server
     * older than the echo sends none.
     */
    offerId: z.number().int().optional(),
});
export type OfferFrame = z.infer<typeof OfferFrameSchema>;

/** A trickled ICE candidate. */
export const IceFrameSchema = z.object({
    ice: z.object({
        candidate: z.string(),
        /** The data channel's media line, sent as the number 128, not as a string. */
        sdpMid: z.number(),
        sdpMLineIndex: z.number().int(),
        usernameFragment: z.string(),
    }),
});
export type IceFrame = z.infer<typeof IceFrameSchema>;

/** One camera the browser cannot decode and the server cannot transcode. */
export const CodecUnsupportedFrameSchema = z.object({
    error: z.literal('codecUnsupported'),
    deviceId: DeviceIdSchema,
    /** FFmpeg's short name of the codec, such as `hevc`. */
    codec: z.string(),
});
export type CodecUnsupportedFrame = z.infer<typeof CodecUnsupportedFrameSchema>;

/** A signalling frame, named by the key that told its kind. */
export type SignallingFrame =
    KeyedFrame<'sdp', OfferFrame> |
    KeyedFrame<'ice', IceFrame> |
    KeyedFrame<'error', CodecUnsupportedFrame>;

/** The stream statuses a media_server sends; it declares 100 too, and never sends it. */
export const StreamStatus = {
    Ok: 200,
    MovedPermanently: 301,
    NotFound: 404,
    Gone: 410,
} as const;
export type StreamStatus = (typeof StreamStatus)[keyof typeof StreamStatus];

/** An RTP timestamp is an unsigned 32-bit counter, which wraps to 0 after this value. */
const RTP_TIMESTAMP_MAX = 0xFFFF_FFFF;

/** Maps a camera's RTP clock to UTC; the tiles place presented frames in time through these. */
export const SyncPointFrameSchema = z.object({
    deviceId: DeviceIdSchema,
    timestampUs: z.number().int(),
    rtpTimestamp: z.number().int().min(0).max(RTP_TIMESTAMP_MAX),
    /** The same mapping for the camera's audio, when it has audio. */
    audio: z.object({
        timestampUs: z.number().int(),
        rtpTimestamp: z.number().int().min(0).max(RTP_TIMESTAMP_MAX),
    }).optional(),
});
export type SyncPointFrame = z.infer<typeof SyncPointFrameSchema>;

/** Where a camera's stream stands after a command, or after the archive changed under it. */
export const StreamStatusFrameSchema = z.object({
    deviceId: DeviceIdSchema,
    /** A double on the wire, -1 when live. */
    timestampMs: z.number(),
    status: z.enum(StreamStatus),
    /** The command's seq; absent when the command had none. */
    seq: z.number().int().optional(),
});
export type StreamStatusFrame = z.infer<typeof StreamStatusFrameSchema>;

/** Where each camera's paused playback settled after a frame step. */
export const PositionsFrameSchema = z.object({
    seq: z.number().int(),
    positions: listOf(z.object({
        deviceId: DeviceIdSchema,
        timestampUs: z.number().int(),
    }), 'positions', { getKey: keyBy('deviceId') }),
});
export type PositionsFrame = z.infer<typeof PositionsFrameSchema>;

/**
 * A metadata frame. Its packet is a discriminated union of its own, which the decoder parses in
 * a second step, so that a packet of an undeclared type is an unknown frame.
 */
export const MetadataFrameSchema = z.object({
    metadata: z.unknown(),
});

/**
 * Where a camera's video travels. The browser's WebRTC path refuses H.264 above the level 5.2
 * frame size (4096x2304) before any decoder sees it, so the server sends such frames as binary
 * data channel messages instead (see VideoMessage), and the player decodes them itself.
 */
export const VideoTransport = {
    Rtp: 'rtp',
    DataChannel: 'dataChannel',
} as const;
export type VideoTransport = (typeof VideoTransport)[keyof typeof VideoTransport];

/** The server's word, once per change, on where a camera's video travels from now on. */
export const VideoTransportFrameSchema = z.object({
    deviceId: DeviceIdSchema,
    videoTransport: z.enum(VideoTransport),
});
export type VideoTransportFrame = z.infer<typeof VideoTransportFrameSchema>;

/** The first bytes of every video chunk, so that a stray binary message is told from one. */
export const VIDEO_MESSAGE_MAGIC = 'OCDV';

/** The codecs a video message may carry, as its header numbers them. */
export const VideoMessageCodec = {
    H264: 1,
} as const;

/** The bits of a message's flags the player reads; the media_server's MediaFlags values. */
export const MediaFlag = {
    KeyFrame: 0x1,
    /** A frame between a seek's key frame and its target, shown only by the reveal. */
    PreRoll: 0x10,
} as const;

/**
 * The header of one video message of the data channel, one frame each, as the decoder reads it
 * off the bytes (big-endian, the device id as RFC 4122 bytes). The messages ride unordered and are
 * retransmitted only while fresh, so a frameIndex may be skipped for good.
 */
export const VideoMessageHeaderSchema = z.object({
    magic: z.literal(VIDEO_MESSAGE_MAGIC),
    version: z.literal([1, 2]),
    codec: z.enum(VideoMessageCodec),
    /** The frame size of the camera's codec parameters; 0 when unknown, and in version 1. */
    width: z.number().int().min(0),
    height: z.number().int().min(0),
    flags: z.number().int().min(0),
    deviceId: DeviceIdSchema,
    frameIndex: z.number().int().min(0),
    rtpTimestamp: z.number().int().min(0).max(RTP_TIMESTAMP_MAX),
    timestampUs: z.number().int(),
});
type VideoMessageHeader = z.infer<typeof VideoMessageHeaderSchema>;

/** One video message: its header and the Annex B access unit of its frame. */
export interface VideoMessage extends VideoMessageHeader {
    data: Uint8Array;
}

/** A data-channel frame, named by the key that told its kind. */
export type DataChannelFrame =
    KeyedFrame<'rtpTimestamp', SyncPointFrame> |
    KeyedFrame<'status', StreamStatusFrame> |
    KeyedFrame<'positions', PositionsFrame> |
    KeyedFrame<'metadata', MetadataPacket> |
    KeyedFrame<'videoTransport', VideoTransportFrame>;
